import { buffers, type Channel } from 'redux-saga';
import {
  actionChannel,
  all,
  call,
  fork,
  put,
  select,
  take,
  takeEvery,
} from 'redux-saga/effects';

import type {
  BulkPartFileGeometricAnalysisData,
  GeometricAnalysisFeature,
  GeometricAnalysisFeedbackItem,
  PartFileGeometricAnalysisData,
} from '@frontend/api';
import type { AdditionalViewerContent } from '@frontend/partviewer';

import { FEATURE_LOADING_TYPES } from 'modules/common/viewer/cadViewer/constants';
import {
  getPartFileByUuid,
  getPartFilesByUuids,
} from 'modules/common/viewer/data/reducer';
import { getActiveFileUUID } from 'modules/viewerPage/data/reducer';

import partService from 'services/partService';

import type { ActionOfType } from 'store/types';

import {
  types,
  featureLoading,
  fetchPartFileGeometricAnalysisData,
  geometricAnalysisQueue,
  partFileGeometricAnalysis,
  type GeometricAnalysisDataAction,
} from './actions';
import {
  getCachedFeatureData,
  getGeometricAnalysisDataForPartFile,
  getGeometricAnalysisQueue,
  getIsAnyGeometricAnalysisDataFetching,
} from './reducer';
import type { PreparedPartFileGeometricAnalysisData } from './types';
import { processFeatures, processFeedbackItems, processSetups } from './utils';

export default function* root() {
  yield fork(watchGeometricAnalysisQueue);
  yield takeEvery(types.FEATURE_LOADING_REQUEST, watchFeatureLoadingRequest);
  yield takeEvery(
    types.BULK_GEOMETRIC_ANALYSIS_RECEIVE,
    watchReceiveBulkGeometricAnalysisData,
  );
}

function* fetchGeometricAnalysisData(partFileUuid: string) {
  try {
    yield put(
      fetchPartFileGeometricAnalysisData.init({
        partFileUuid,
      }),
    );

    const { features, feedbackItems, interrogationFiles, setups } = yield call(
      partService.partFiles.geometricAnalysisData.get,
      partFileUuid,
    );

    const partFile: ReturnType<typeof getPartFileByUuid> = yield select(
      getPartFileByUuid,
      partFileUuid,
    );
    let flatFeatures: GeometricAnalysisFeature[] = [];
    let flatFeedbackItems: GeometricAnalysisFeedbackItem[] = [];
    if (partFile?.flatFileUuid) {
      const flatFileData: PartFileGeometricAnalysisData = yield call(
        partService.partFiles.geometricAnalysisData.get,
        partFile.flatFileUuid,
      );
      flatFeatures = flatFileData.features;
      flatFeedbackItems = flatFileData.feedbackItems;
    }

    const cachedFeatureData: ReturnType<typeof getCachedFeatureData> =
      yield select(getCachedFeatureData);

    const processedFeatures = processFeatures({
      features,
      flatFeatures,
      cachedFeatureData,
    });
    const processedFeedbackItems = processFeedbackItems({
      feedbackItems,
      flatFeedbackItems,
      cachedFeatureData,
    });
    const processedSetups = processSetups(setups);

    yield put(
      fetchPartFileGeometricAnalysisData.success({
        geometricAnalysisData: {
          features: processedFeatures,
          feedbackItems: processedFeedbackItems,
          interrogationFiles,
          setups: processedSetups,
        },
        partFileUuid,
      }),
    );
  } catch (error) {
    console.error('Error in watchFetchGeometricAnalysisData', error);
    yield put(fetchPartFileGeometricAnalysisData.error({ partFileUuid }));
  }
}

type FlatGeometricAnalysisData = BulkPartFileGeometricAnalysisData & {
  flatFeatures?: GeometricAnalysisFeature[];
  flatFeedbackItems?: GeometricAnalysisFeedbackItem[];
};

function* watchReceiveBulkGeometricAnalysisData(
  action: ActionOfType<
    GeometricAnalysisDataAction,
    typeof types.BULK_GEOMETRIC_ANALYSIS_RECEIVE
  >,
) {
  const activePartFileUUID: string | null = yield select(getActiveFileUUID);

  const partFileUuids = action?.payload?.partFileUuids
    ? [...action.payload.partFileUuids]
    : [];
  if (!partFileUuids.length) {
    return;
  }

  // add child component UUID if starting as active file, because child components are not included in partFile list form tech data package
  // https://paperlessparts.atlassian.net/browse/PP-20471
  if (activePartFileUUID && !partFileUuids.includes(activePartFileUUID)) {
    partFileUuids.push(activePartFileUUID);
  }

  try {
    for (const partFileUuid of partFileUuids) {
      yield put(
        fetchPartFileGeometricAnalysisData.init({
          partFileUuid,
        }),
      );
    }

    const geometricAnalysisDatas: FlatGeometricAnalysisData[] = yield call(
      partService.partFiles.bulkGeometricAnalysisData.get,
      partFileUuids,
    );

    const partFiles: ReturnType<typeof getPartFilesByUuids> = yield select(
      getPartFilesByUuids,
      partFileUuids,
    );

    const flatPartFileUuids = partFiles
      .filter((partFile) => partFile?.flatFileUuid)
      // NB: We coerce here because the type is not properly inferred from the filter, even though
      // it's filtering out all falsy values, narrowing all values from null/undefined/empty strings to non-empty strings
      .map((partFile) => partFile.flatFileUuid as string);

    let flatGeometricAnalysisDatas: FlatGeometricAnalysisData[] = [];

    if (flatPartFileUuids.length) {
      flatGeometricAnalysisDatas = yield call(
        partService.partFiles.bulkGeometricAnalysisData.get,
        flatPartFileUuids,
      );
    }

    for (const {
      features,
      feedbackItems,
      uuid,
    } of flatGeometricAnalysisDatas.values()) {
      const mainFileUuidOfFlatFileUuid = partFiles.find(
        (partFile) => uuid === partFile.flatFileUuid,
      )?.uuid;

      if (mainFileUuidOfFlatFileUuid) {
        const mainFile = geometricAnalysisDatas.find((geoData) => geoData.uuid);
        if (mainFile) {
          mainFile.flatFeatures = features;
          mainFile.flatFeedbackItems = feedbackItems;
        }
      }
    }

    const cachedFeatureData: ReturnType<typeof getCachedFeatureData> =
      yield select(getCachedFeatureData);

    for (const {
      features,
      feedbackItems,
      interrogationFiles,
      setups,
      flatFeatures = [],
      flatFeedbackItems = [],
      uuid,
    } of geometricAnalysisDatas.values()) {
      const processedFeatures = processFeatures({
        features,
        flatFeatures,
        cachedFeatureData,
      });
      const processedFeedbackItems = processFeedbackItems({
        feedbackItems,
        flatFeedbackItems,
        cachedFeatureData,
      });
      const processedSetups = processSetups(setups);

      yield put(
        fetchPartFileGeometricAnalysisData.success({
          geometricAnalysisData: {
            features: processedFeatures,
            feedbackItems: processedFeedbackItems,
            interrogationFiles,
            setups: processedSetups,
          },
          partFileUuid: uuid,
        }),
      );
    }
  } catch (error) {
    console.error('Error in watchFetchGeometricAnalysisData', error);
    for (const partFileUuid of partFileUuids) {
      yield put(fetchPartFileGeometricAnalysisData.error({ partFileUuid }));
    }
  }
}

function* popGeometricAnalysisQueue() {
  /*
  Responsible for managing the geometric analysis queue to ensure there is only ever one task being run at a time.
  */
  try {
    // Drain the queue rather than taking one entry per enqueue: an entry added
    // while another fetch (e.g. the bulk fetch) is in flight would otherwise sit
    // until some later enqueue, so a refetch requested after an interrogation
    // finishes could never run.
    for (;;) {
      const isFetchingGeometricAnalysis: boolean = yield select(
        getIsAnyGeometricAnalysisDataFetching,
      );
      if (isFetchingGeometricAnalysis) {
        yield take([
          types.FETCH_PART_FILE_GEOMETRIC_ANALYSIS_DATA_SUCCESS,
          types.FETCH_PART_FILE_GEOMETRIC_ANALYSIS_DATA_ERROR,
        ]);
        continue;
      }
      const queue: ReturnType<typeof getGeometricAnalysisQueue> = yield select(
        getGeometricAnalysisQueue,
      );
      if (!queue.length) return;
      const nextPartFileUuid = queue[0];
      yield call(fetchGeometricAnalysisData, nextPartFileUuid);
      yield put(
        geometricAnalysisQueue.remove({ partFileUuid: nextPartFileUuid }),
      );
    }
  } catch (error) {
    console.error('Error in watchGeometricAnalysisQueue', error);
  }
}

function* watchGeometricAnalysisQueue() {
  //Responsible for managing the partfile cad data queue to ensure there is only ever one task being run at a time.

  const chan: Channel<
    ActionOfType<
      GeometricAnalysisDataAction,
      | typeof types.GEOMETRIC_ANALYSIS_QUEUE_ADD_TO_END
      | typeof types.GEOMETRIC_ANALYSIS_QUEUE_ADD_TO_START
    >
  > = yield actionChannel(
    [
      types.GEOMETRIC_ANALYSIS_QUEUE_ADD_TO_END,
      types.GEOMETRIC_ANALYSIS_QUEUE_ADD_TO_START,
    ],
    buffers.expanding(25),
  );
  for (;;) {
    yield take(chan);
    yield call(popGeometricAnalysisQueue);
  }
}

function* watchFeatureLoadingRequest(
  action: ActionOfType<
    GeometricAnalysisDataAction,
    typeof types.FEATURE_LOADING_REQUEST
  >,
) {
  /*
  This function is responsible for fetching additional tessalation data for features and feedback items.

  Long term, this fetching and caching should be the responsibility of @frontend/partviewer.
  */
  try {
    const { payload } = action;
    const { features, partFileUuid, type } = payload;
    // Only items with a content blob need loading.
    const loadableFeatures = features.filter(
      (feature): feature is typeof feature & { authenticatedUrl: string } =>
        feature.authenticatedUrl != null,
    );

    const batchSize = 200;
    const featureContentsById: Record<number, AdditionalViewerContent> = {};
    for (
      let batchCount = 0;
      batchCount < Math.ceil(loadableFeatures.length / batchSize);
      batchCount++
    ) {
      const startInd = batchSize * batchCount;
      const endInd = (batchCount + 1) * batchSize;
      const section = loadableFeatures.slice(startInd, endInd);
      const featureContents: AdditionalViewerContent[] = yield all(
        section.map((feature) =>
          fetch(feature.authenticatedUrl).then((response) => response.json()),
        ),
      );
      for (const i in section) {
        featureContentsById[section[i].id] = featureContents[i];
      }
    }

    // update stored features
    const geometricAnalysisData: ReturnType<
      typeof getGeometricAnalysisDataForPartFile
    > = yield select(getGeometricAnalysisDataForPartFile, partFileUuid);
    if (geometricAnalysisData) {
      const featureIds = features.map((f) => f.id);
      let nextGeometricAnalysisData: PreparedPartFileGeometricAnalysisData;
      if (type === FEATURE_LOADING_TYPES.FEATURE) {
        nextGeometricAnalysisData = {
          ...geometricAnalysisData,
          features: geometricAnalysisData.features.map((feature) => {
            if (featureIds.includes(feature.id)) {
              return {
                ...feature,
                additionalViewerContent:
                  featureContentsById?.[feature.id] ?? {},
                needsLoading: false,
              };
            } else {
              return feature;
            }
          }),
        };
        // Exhaustive pattern match with else.
      } else {
        nextGeometricAnalysisData = {
          ...geometricAnalysisData,
          feedbackItems: geometricAnalysisData.feedbackItems.map(
            (feedbackItem) => {
              if (featureIds.includes(feedbackItem.id)) {
                return {
                  ...feedbackItem,
                  additionalViewerContent:
                    featureContentsById?.[feedbackItem.id] ?? {},
                  needsLoading: false,
                };
              } else {
                return feedbackItem;
              }
            },
          ),
        };
      }

      yield put(
        partFileGeometricAnalysis.receive({
          geometricAnalysisData: nextGeometricAnalysisData,
          partFileUuid,
        }),
      );
    }

    // update cached feature info with success dispatch
    yield put(featureLoading.success({ featureContentsById, type }));
  } catch (error) {
    console.error('Error loading feature contents', error);
    const { features, type } = action.payload;
    if (features) {
      yield put(
        featureLoading.error({
          featureIds: (features || []).map((f) => f.id),
          type,
        }),
      );
    }
  }
}
